import { useMemo } from 'react';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Spinner } from '@/components/ui/Spinner';
import type { Patient } from '@/types/patient';
import { getPatientFullName } from '@/types/patient';

interface PatientSelectorProps {
  patients: Patient[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  isLoading?: boolean;
}

export function PatientSelector({
  patients,
  selectedIds,
  onChange,
  isLoading = false,
}: PatientSelectorProps) {
  const selectedPatients = useMemo(() => {
    return patients.filter((p) => selectedIds.includes(p.id));
  }, [patients, selectedIds]);

  const handleChange = (selected: Patient[]) => {
    onChange(selected.map((p) => p.id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-3">
        <Spinner size="sm" />
        <span className="text-sm text-muted">Loading patients...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-foreground">
        Patients
      </label>
      <MultiSelect
        options={patients}
        selected={selectedPatients}
        onChange={handleChange}
        getKey={(p) => p.id}
        getLabel={(p) => `${getPatientFullName(p)} (${p.id})`}
        getSecondaryLabel={(p) => `${p.totalActivities} activities`}
        placeholder="Select patients..."
        searchPlaceholder="Search patients..."
        selectAllLabel="Select all patients"
        maxDisplayed={5}
      />
      {selectedIds.length === 0 && patients.length > 0 && (
        <p className="text-xs text-muted">
          No patients selected - all {patients.length} patients will be included
        </p>
      )}
    </div>
  );
}
